/**
 * Sessão do usuário (F1.2) — a camada que as telas consultam para saber quem está e o que pode.
 *
 * Regras que este arquivo carrega, todas do contrato:
 *  - **Bloqueio vale na hora**: o servidor decide (`app.sessao_valida()`), não o app. O que fazemos
 *    aqui é perguntar de novo em toda ação sensível, para a tela não mentir para o funcionário.
 *  - **Ação de Admin exige o segundo fator** (AAL2). O app não esconde o botão para enganar: mostra
 *    e explica o que falta.
 *  - **Perder a sessão nunca pode perder trabalho**: quem guarda o que foi feito é o aparelho
 *    (fila local), não a sessão.
 */
import { supabase, nivelDeGarantia } from '../lib/supabase.js';

export interface UsuarioSessao {
  id: string;
  nome: string;
  email: string;
  funcao: string;
  capacidades: string[];
  empresa_id: string;
  filial_padrao_id: string | null;
  precisa_segundo_fator: boolean;
  aal: 'aal1' | 'aal2' | 'nenhum';
}

/** Lê o usuário do banco (perfil + capacidades). `null` = não autenticado ou bloqueado. */
export async function carregarSessao(): Promise<UsuarioSessao | null> {
  const cliente = supabase();
  const { data: auth } = await cliente.auth.getUser();
  if (!auth?.user) return null;

  const { data, error } = await cliente.rpc('meu_perfil');
  if (error || !data) return null;

  const perfil = (Array.isArray(data) ? data[0] : data) as {
    usuario_id: string;
    nome: string;
    email: string;
    funcao: string;
    capacidades: string[];
    empresa_id: string;
    filial_padrao_id: string | null;
    politica_2fa: 'obrigatorio' | 'opcional';
  };

  const aal = await nivelDeGarantia();
  return {
    id: perfil.usuario_id,
    nome: perfil.nome,
    email: perfil.email,
    funcao: perfil.funcao,
    capacidades: perfil.capacidades ?? [],
    empresa_id: perfil.empresa_id,
    filial_padrao_id: perfil.filial_padrao_id,
    precisa_segundo_fator: perfil.politica_2fa === 'obrigatorio' && aal !== 'aal2',
    aal,
  };
}

export function pode(sessao: UsuarioSessao | null, capacidade: string): boolean {
  return !!sessao?.capacidades.includes(capacidade);
}

/**
 * Por que a tela precisa disto: quando o botão não aparece, o funcionário precisa saber POR QUÊ
 * (estado "sem permissão" da tabela A). Devolve a frase pronta ou `null` quando pode agir.
 */
export function porQueNaoPode(sessao: UsuarioSessao | null, capacidade: string, exigeAal2 = false): string | null {
  if (!sessao) return 'Entre novamente para continuar.';
  if (!sessao.capacidades.includes(capacidade)) {
    return 'Você não tem permissão para isso — peça a um gestor.';
  }
  if (exigeAal2 && sessao.aal !== 'aal2') {
    return 'Esta ação exige o segundo fator. Confirme o código do seu aplicativo autenticador.';
  }
  return null;
}

export async function sair(): Promise<void> {
  await supabase().auth.signOut();
}
